export const PORTFOLIO_CASE_STUDIES = [
  {
    slug: "hc-apparel-commerce",
    number: "01",
    title: "HC Apparel Commerce Experience",
    shortTitle: "HC Apparel",
    discipline: "Creative direction · Website · Commerce systems",
    summary:
      "A live customer-first website that pairs clear apparel branding with product discovery, DTF pricing, artwork handling, freight verification, checkout, and staff order operations.",
    challenge:
      "Custom print orders carry more decisions than a standard product purchase. Customers need clear pricing and artwork guidance, while operations teams need the original file, dimensions, payment state, freight, and fulfillment decisions to stay connected without exposing private supplier information.",
    approach: [
      "Designed the public storefront around clear garment discovery, responsive navigation, and direct paths into custom printing.",
      "Built the DTF journey around price discovery first, then artwork, specifications, freight, and payment.",
      "Kept the customer interface warm and simple while giving staff a separate, permission-aware order workspace.",
      "Built resumable order states and duplicate protection so artwork and specifications remain attached throughout checkout.",
    ],
    outcome:
      "A responsive production storefront with a live DTF calculator, artwork-preserving order flow, verified freight step, and supplier submission kept behind a separate approval gate.",
    capabilities: ["Experience strategy", "Commerce UX", "Design systems", "Workflow engineering"],
    accent: "#f1b24a",
    links: [
      { label: "Visit Live Website", url: "https://www.ilovehcapparel.net" },
      { label: "Explore DTF Prints", url: "https://www.ilovehcapparel.net/DTFTransfers" },
    ],
    cardScreenshots: ["hc-home-desktop", "hc-dtf-mobile"],
    screenshots: [
      {
        id: "hc-home-desktop",
        src: "/creative/hc-home-desktop.jpg",
        width: 1425,
        height: 891,
        alt: "HC Apparel live homepage on desktop with green navigation, apparel hero, and shopping calls to action",
        caption: "Live HC Apparel homepage · desktop",
      },
      {
        id: "hc-home-mobile",
        src: "/creative/hc-home-mobile.jpg",
        width: 375,
        height: 812,
        alt: "HC Apparel live homepage in its mobile layout",
        caption: "Live HC Apparel homepage · phone",
      },
      {
        id: "hc-dtf-desktop",
        src: "/creative/hc-dtf-desktop.jpg",
        width: 1425,
        height: 891,
        alt: "HC Apparel DTF Prints page on desktop showing transfer ordering choices and branded benefit imagery",
        caption: "Live DTF Prints interface · desktop",
      },
      {
        id: "hc-dtf-mobile",
        src: "/creative/hc-dtf-mobile.jpg",
        width: 375,
        height: 812,
        alt: "HC Apparel DTF Prints page in its mobile layout",
        caption: "Live DTF Prints interface · phone",
      },
    ],
  },
  {
    slug: "heart-command-center",
    number: "02",
    title: "Heart Command Center",
    shortTitle: "Heart Command Center",
    discipline: "KPI dashboards · CRM workflows · Operational systems",
    summary:
      "A private, local-first operations workspace connecting KPI visibility, financial evidence, CRM records, and accountable next actions across desktop and phone.",
    challenge:
      "Financial records, customer work, integrations, and recurring responsibilities can become fragmented across tools. Leadership needed facts, decisions, and follow-through to stay connected without treating missing data as certainty.",
    approach: [
      "Built a dedicated Heart Command Center interface separate from HC Apparel’s website Business Action Center and customer-facing admin.",
      "Organized KPI reporting, finance, CRM, invoicing, receipts, mileage, and tasks around source evidence and explicit status.",
      "Separated operational state from payment and fulfillment state so each signal keeps its proper meaning.",
      "Designed responsive summaries and navigation for quick review while preserving deeper local audit trails.",
    ],
    outcome:
      "A private operating workspace with dashboards, accounting views, reports, tasks, local-first records, and secure integrations. The portfolio uses a no-proxy demo state so no private production records are published.",
    capabilities: ["Information architecture", "Operational UX", "Financial data modeling", "Automation"],
    accent: "#62d0b1",
    mediaNotice: "Demo interface · production data and private integrations omitted",
    cardScreenshots: ["hcc-desktop", "hcc-mobile"],
    screenshots: [
      {
        id: "hcc-desktop",
        src: "/creative/hcc-desktop-demo.jpg",
        width: 1265,
        height: 712,
        alt: "Heart Command Center desktop dashboard rendered from the real application with production data omitted",
        caption: "Actual Heart Command Center components · desktop demo state",
      },
      {
        id: "hcc-mobile",
        src: "/creative/hcc-mobile-demo.jpg",
        width: 375,
        height: 812,
        alt: "Heart Command Center mobile dashboard rendered from the real application with production data omitted",
        caption: "Actual Heart Command Center components · phone demo state",
      },
    ],
  },
  {
    slug: "attitude-of-service",
    number: "03",
    title: "The Attitude of Service",
    shortTitle: "The Attitude of Service",
    discipline: "Creative direction · Website · Customer experience",
    statusLabel: "Currently offline",
    summary:
      "A completed client website that established the Metro Atlanta transportation brand online with clear service information, facility-partner content, click-to-call contact, and a ride-booking entry point. Currently offline.",
    challenge:
      "The transportation business needed a credible, responsive digital presence that expressed its safe, compassionate, and reliable service promise while making the path to call, explore services, or begin a ride booking immediately clear.",
    approach: [
      "Extended the company’s green-and-gold identity into a clear transportation-focused homepage and responsive navigation system.",
      "Organized the experience around Services, Facility Partners, Book a Ride, and Contact so visitors could quickly choose the right next step.",
      "Placed Book a Ride and Call Now actions prominently in both the navigation and hero instead of making customers search for assistance.",
      "Used trust-centered messaging and a passenger-care promise to connect the brand story to the customer experience.",
    ],
    outcome:
      "A completed client website with branded service discovery, facility-partner information, direct phone contact, and a clear ride-booking path. The production site is currently offline, so this case study uses an authentic archived project capture and does not offer a live-site link.",
    capabilities: ["Responsive web design", "Service information architecture", "Booking UX", "Customer communication"],
    accent: "#d2a84b",
    mediaNotice: "Authentic archived project capture · completed client work · currently offline",
    cardScreenshots: ["attitude-home-archived"],
    screenshots: [
      {
        id: "attitude-home-archived",
        src: "/creative/attitude-home-archived.jpg",
        width: 1795,
        height: 935,
        alt: "Archived The Attitude of Service homepage with green and gold navigation, transportation hero, Book a Ride, and Call Now actions",
        caption: "Authentic archived homepage preview · desktop",
      },
    ],
  },
];

export const findCaseStudy = (slug) =>
  PORTFOLIO_CASE_STUDIES.find((study) => study.slug === slug);
